import { GameName } from 'constants/difficulty';
import { DebugGA } from 'game/CustomConsole';
import { Store } from 'game/store/gameStore';

export function GAEvent(event: GAEventNames | string, eventData: { [key: string]: number | string } = {}) {
    const data = filterEventData(eventData, Store.name, event);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: event, // The name of the event you want to track
        name: Store.name,
        ...data, // Event data or parameters
    });
    DebugGA(event + '" => ' + JSON.stringify(data));
}

interface ExcludesMapping {
    eventName: GAEventNames;
    excludes: string[];
}
export function filterEventData(data: { [key: string]: string | number }, gameName: GameName, eventName: GAEventNames | string) {
    const excludesMapping: Record<string, ExcludesMapping[]> = {
        [GameName.PYRAMID]: pyramidExcludedProperties,
    };
    const commonExcludes: Record<string, string[]> = {
        [GameName.PYRAMID]: ['difficulty', 'difficultySelected'],
    };

    const excludesData = excludesMapping[gameName]?.find((item) => item.eventName === eventName);

    let result = {...data};

    if (excludesData) {

        excludesData.excludes.forEach((field) => {
            if (field in result) {
                delete result[field];
            }
        });

    }

    if (commonExcludes[gameName]) {
        commonExcludes[gameName].forEach((field) => {
            if (field in result) {
                delete result[field];
            }
        });
    }

    return result;
}

export enum GAEventNames {
    GAME_START = 'game_start',
    GAME_WIN = 'game_win',
    GAME_RESTART = 'game_restart',
    GAME_UNDO = 'game_undo',
    GAME_RULES_OPEN = 'game_rules_opened',
    GAME_SETTINGS_OPEN = 'game_settings_opened',
    GAME_DESIGN_OPEN = 'game_design_opened',

    GAME_SETTINGS_SOUNDS = 'game_settings_sounds',
    GAME_SETTINGS_HAND = 'game_settings_hand',
    GAME_SETTINGS_AUTOCOMPLETE = 'game_settings_autocomplete',
    GAME_DESIGN_FRONT = 'game_design_front',
    GAME_DESIGN_BACK = 'game_design_back',
    GAME_DESIGN_BACKGROUND = 'game_design_background',
    OPEN_NEW_GAME_POPUP = 'game_new_game_popup_opened',
    NEW_GAME_CLICK = 'game_new_game_play',
    WIN_POPUP_NEW_GAME_CLICK = 'game_win_popup_play',
    GAME_SETTINGS_HIGHLIGHT = 'game_settings_highlighting',
    GAME_SETTINGS_AUTOPLAY = 'game_settings_autoplay',

    FACEBOOK_BANNER_VIEW = 'facebook_banner_view',
    FACEBOOK_BANNER_CLOSE = 'facebook_banner_close',
    FACEBOOK_BANNER_FOLLOW = 'facebook_banner_follow',
    SHOW_SHARE = 'share_menu_click',
    SHARE_SUCCESSFUL = 'share_successful',
    HINT_CLICK = 'game_hint',

    SHOW_NO_HINT_MODAL = 'game_no_hints_popup',
    NO_HINT_NEW_GAME = 'game_no_hints_popup_new_game',
    NO_HINT_RESTART_GAME = 'game_no_hints_popup_restart',
    NO_KEEP_PLAY = 'game_no_hints_popup_keep_playing',

    ADS_BREAK_POPUP = 'ad_break_preview', //counter
    ADS_BREAK_START = 'ad_break_start', // ad shown
    ADS_BREAK_DONE = 'ad_break_done',

    ADS_REWARDER_CLICK = 'ad_rewarded_click',
    ADS_REWARDER_SKIP = 'ad_rewarded_skip',
    ADS_REWARDER_RECEIVE = 'ad_rewarded_receive',
    ADS_REWARDER_ERROR = 'ad_rewarded_receive_ad_not_filled',

    MAGIC_WAND_CLICK = 'game_mw',
    MAGIC_WAND_ONBOARDING = 'game_mw_onboarding',
    MAGIC_WAND_ONBOARDING_TRY = 'game_mw_onboarding_try',

    MAGIC_WAND_ONBOARDING_CLOSE = 'game_mw_onboarding_close',
    MAGIC_WAND_NO_MOVES = 'game_no_mw_popup',

    MAGIC_WAND_NO_MOVES_CLOSE = 'game_no_mw_popup_close',
    MAGIC_WAND_NO_MOVES_CONFIRM = 'game_no_mw_popup_watch_ad',
    MAGIC_WAND_NO_NEEDED = 'game_mw_no_needed',

    OUT_MOVE_WAND_POPUP = 'game_no_hints_mw_popup',
    OUT_MOVE_WAND_POPUP_CLOSE = 'game_no_hints_mw_popup_close',
    OUT_MOVE_WAND_POPUP_CONFIRM = 'game_no_hints_mw_popup_use',

    OUT_MOVE_AD_POPUP = 'game_no_hints_no_mw_popup',
    OUT_MOVE_AD_CLOSE = 'game_no_hints_no_mw_popup_close',
    OUT_MOVE_AD_CONFIRM = 'game_no_hints_no_mw_popup_watch_ad',

    FEEDBACK_SHOW = 'feedback_banner',
    FEEDBACK_NOT_NOW = 'feedback_banner_not_now',
    FEEDBACK_SEND = 'feedback_banner_send',
    FEEDBACK_ERROR = 'feedback_banner_failed',
    AD_BLOCKER_POPUP_SHOW = 'ad_adblocker_popup',
    AD_BLOCKER_POPUP_CLOSE = 'ad_adblocker_popup_close',
    NOTIFICATION_POPUP_SHOW = 'push_notifications_view',
    NOTIFICATION_POPUP_SUBSCRIBE = 'push_notifications_subscribe',
    NOTIFICATION_POPUP_SKIP = 'push_notifications_not_now',

    STORIES_USE_TICKET_BANNER_CLOSE = 'stories_use_ticket_banner_close',
    STORIES_USE_TICKET_BANNER_OPEN = 'stories_use_ticket_banner_open',
    STORIES_USE_TICKET_BANNER_VIEW = 'stories_use_ticket_banner_view',
    DIFFICULTY_SET_UP = 'difficulty_set_up',
    TUTORIAL_STEP = 'tutorial_step_',
    TUTORIAL_SHOW_BANNER = 'tutorial_start_banner_view',
    TUTORIAL_READ_RULES = 'tutorial_start_banner_read',
    TUTORIAL_MODAL_CANCEL = 'tutorial_start_banner_close',
    TUTORIAL_MODAL_START = 'tutorial_start_banner_start',
    TUTORIAL_SKIP_CONFIRM = 'tutorial_skip_banner_skip',
    TUTORIAL_SKIP_BTN_CLICK = 'tutorial_skip_button',
    TUTORIAL_RESTART_BTN_CLICK = 'tutorial_restart_button',
    TUTORIAL_SKIP_POPUP = 'tutorial_skip_banner_view',
    TUTORIAL_RESTART_POPUP = 'tutorial_restart_banner_view',
    TUTORIAL_SKIP_CANCEL = 'tutorial_skip_banner_close',
    TUTORIAL_COMPLETE_POPUP = 'tutorial_complete_banner_view',
    TUTORIAL_COMPLETE_RESTART = 'tutorial_complete_banner_restart',
    TUTORIAL_COMPLETE_CONFIRM = 'tutorial_complete_banner_play',
    TUTORIAL_RESTART_CONFIRM = 'tutorial_restart_banner_restart',
    TUTORIAL_RESTART_CANCEL = 'tutorial_restart_banner_close',
    TUTORIAL_WELCOME_POPUP_VIEW = 'tutorial_start_notification_view',
    TUTORIAL_WELCOME_POPUP_START = 'tutorial_start_notification_start',
    TUTORIAL_WELCOME_POPUP_SKIP = 'tutorial_start_notification_skip',


    MISSION_BUTTON_CLICK = 'mission_icon',
    MISSION_INFO_BUTTON_CLICK = 'mission_inf_icon',
    MISSION_CLOSE_BUTTON_CLICK = 'mission_close',
    MISSION_ALL_COMPLETED = 'mission_all_completed', 
    MISSION_COMPLETED = 'mission_completed',
    MISSION_REWARD_CLAIMED = 'mission_reward_claimed',
    MISSION_REWARD_CONTINUE = 'mission_reward_continue',
    MISSION_UNLOCK_BUTTON = 'mission_unlock_button',
    MISSION_CLICK_ON_WIN_POPUP = 'mission_win_popup',

    STORIES_WIN_POPUP_BUTTON = 'stories_win_popup_button', //
    STORIES_BUTTON_CLICK = 'stories_button', // 
    STORIES_INFO_BUTTON_CLICK = 'stories_inf_icon', //
    STORIES_TAB_UNLOCK = 'stories_tab_unlock', //
    STORIES_TAB_COMPLETED = 'stories_tab_completed', //
    STORIES_NO_TICKET_BANNER_VIEW = 'stories_no_ticket_banner_view', //
    STORIES_NO_TICKET_BANNER_CLOSE = 'stories_no_ticket_banner_close', //
    STORIES_VIDEO_BUTTON = 'stories_video_button', //
    STORIES_CONGRATULATIONS_BANNER_VIEW = 'stories_congr_banner_view', //
    STORIES_CONGRATULATIONS_BANNER_PLAY_VIDEO = 'stories_congr_banner_play_video', //
    STORIES_UNLOCK_IMAGE = 'stories_unlock_image', //
    STORIES_VIDEO_CLOSE = 'stories_video_close', //
    STORIES_PREVIEW_IMAGE = 'stories_preview_image', //
    STORIES_SHOW_MORE = 'stories_show_more', //
    STORIES_SHOW_LESS = 'stories_show_less', //
    STORIES_VIEW_STORY = 'stories_view_story', //
    STORIES_PREVIEW_TEXT_ICON_MOBILE = 'stories_preview_text_icon', //
    STORIES_PREVIEW_TEXT_CLOSE_MOBILE = 'stories_preview_text_close', //
    STORIES_UNLOCK_ALL = 'stories_unlock_all',
}


const pyramidExcludedProperties = [
    {
        eventName: GAEventNames.GAME_START,
        excludes: ['type', 'highlighting', 'autoplay', 'autocomplete'],
    },
    {
        eventName: GAEventNames.GAME_WIN,
        excludes: ['type', 'autoplay', 'autocomplete'],
    },
    {
        eventName: GAEventNames.GAME_RESTART,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.GAME_UNDO,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.OPEN_NEW_GAME_POPUP,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.OPEN_NEW_GAME_POPUP,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.NEW_GAME_CLICK,
        excludes: ['type', 'typeSelected'],
    },
    {
        eventName: GAEventNames.WIN_POPUP_NEW_GAME_CLICK,
        excludes: ['type', 'typeSelected'],
    },
    {
        eventName: GAEventNames.SHOW_NO_HINT_MODAL,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.NO_HINT_NEW_GAME,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.NO_HINT_RESTART_GAME,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.NO_KEEP_PLAY,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.HINT_CLICK,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.MAGIC_WAND_CLICK,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.MAGIC_WAND_ONBOARDING_CLOSE,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.MAGIC_WAND_ONBOARDING_TRY,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.MAGIC_WAND_NO_MOVES_CONFIRM,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.OUT_MOVE_WAND_POPUP,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.OUT_MOVE_AD_POPUP,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.MAGIC_WAND_NO_MOVES,
        excludes: ['type'],
    },
    {
        eventName: GAEventNames.MAGIC_WAND_NO_MOVES_CLOSE,
        excludes: ['type'],
    },
];